import { useState, useEffect } from "react";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { ConsentApi } from "../api/Consent";
import FilterIcon from "../shared-components/icons/FilterIcon";
import CopyIcon from "../shared-components/CopyIcon";
import PDFDownloadWrapper from "./PDFDownloadWrapper";
import { useMockContent } from "../api/ConsentApi";
import { formattedDate } from "../utils";

interface TableProps {
  data: ConsentApi[] | undefined;
  header: Array<{ name: string; sortController?: string }>;
  isLoading: boolean;
  hasFilters: (value: string) => boolean;
  openFilter: (value: string) => void;
  onEventComplete: () => void;
}

interface SignatureActionsProps {
  status: string;
  signature_request_id: string;
  signature_id: string;
  onEventComplete: () => void;
}

const Table = ({
  data,
  header,
  isLoading,
  openFilter,
  hasFilters,
  onEventComplete,
}: TableProps) => {
  const SignatureActions: React.FC<SignatureActionsProps> = ({
    status,
    signature_request_id,
    signature_id,
    onEventComplete,
  }) => {
    const { mutateAsync: sendMockedEvent } = useMockContent();

    const getNextButton = (status: string) => {
      switch (status) {
        case "submitted":
          return { label: "View", event: "signing_request.viewed" };
        case "viewed":
          return { label: "Sign", event: "signing_request.recipient.signed" };
        case "signed":
          return { label: "Ready Up", event: "signing_request.completed" };
        default:
          return null;
      }
    };

    const nextButton = getNextButton(status);

    const handleClick = async (event: string) => {
      await sendMockedEvent({
        signatureRequestId: signature_request_id,
        signatureId: signature_id,
        mockEvent: event,
      });
      onEventComplete();
    };

    return (
      <>
        {nextButton && (
          <span onClick={() => handleClick(nextButton.event)}>
            {nextButton.label}
          </span>
        )}
      </>
    );
  };
  const [tableData, setTableData] = useState<ConsentApi[]>([]);
  const swapName = (name: string) => {
    if (name === "ready") {
      return "Completed";
    }
    return name;
  };

  const isMock = (input: string) => {
    return input.lastIndexOf("mock", 0) === 0;
  };

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header) => (
            <div
              className="flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1"
              key={header.name}
            >
              {header.name}
              {header.sortController && (
                <FilterIcon
                  width={16}
                  className={`my-div ${
                    hasFilters(header.sortController) ? "fill-ourGreen" : ""
                  }`}
                  onClick={() => openFilter(header.sortController!)}
                ></FilterIcon>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {isLoading ? <LoadingSpinner /> : ""}
          {tableData.length === 0 && !isLoading ? (
            <div className="flex justify-center p-4 ">
              No Consent Forms found
            </div>
          ) : (
            tableData.map(
              (
                {
                  name,
                  company,
                  status,
                  report_external_id,
                  signature_id,
                  signature_request_id,
                  created_at,
                  is_completed,
                  signed_at,
                },
                i
              ) => (
                <div key={`consent-${i}`}>
                  <div className="flex border-b text-xs leading-4 font-normal py-4 hover:bg-gray-50">
                    <div className="flex px-3 flex-1  items-center font-semibold truncate text-ellipsis w-32 underline">
                      {name}
                    </div>
                    <div className="flex px-3 flex-1 truncate  items-center text-ellipsis w-32">
                      {company}
                    </div>
                    <div className="flex px-3 flex-1  items-center w-32">
                      <span
                        className={`bg-${status} capitalize  text-xs rounded-lg text-grey-800 px-2.5 py-0.5`}
                      >
                        {swapName(status)}
                      </span>
                    </div>
                    <div className="flex px-3 flex-1  items-center w-32">
                      <span
                        className={`capitalize  text-xs rounded-lg text-grey-800 px-2.5 py-0.5`}
                      >
                        {created_at && formattedDate(created_at)}
                      </span>
                    </div>
                    <div className="flex px-3 flex-1  items-center w-32">
                      <span
                        className={`capitalize  text-xs rounded-lg text-grey-800 px-2.5 py-0.5`}
                      >
                        {signed_at && formattedDate(signed_at)}
                      </span>
                    </div>
                    <div className="flex px-3 flex-1 flex-row items-center gap-2 w-12 underline ">
                      {isMock(signature_request_id) && (
                        <SignatureActions
                          status={status}
                          signature_request_id={signature_request_id}
                          signature_id={signature_id}
                          onEventComplete={onEventComplete} // Pass the callback
                        />
                      )}
                      {isMock(signature_request_id) &&
                        is_completed &&
                        "Mock finished"}
                      {!isMock(signature_request_id) && (
                        <PDFDownloadWrapper
                          report_uuid={signature_request_id}
                        ></PDFDownloadWrapper>
                      )}
                    </div>
                    <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32 ">
                      <div className="truncate w-24">{report_external_id}</div>
                      <CopyIcon textToCopy={report_external_id}></CopyIcon>
                    </div>
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>
    </>
  );
};

export default Table;
